import { MutableRefObject, useCallback, memo } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList
} from "@/components/adg4/tabs/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

import { TWC_TAB_DATA, TWCTab } from "./twc-tab-data";

const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const tabsWrapperStyle = css({
    marginInlineStart: "8px",
    marginInlineEnd: "8px"
});

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "10px"
});
const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});
const teamCardStyles = css({
    "boxSizing": "border-box",
    "border": `1px solid transparent`,
    "display": "flex",
    "flexDirection": "column",
    "width": "187px",
    "height": "129px",
    "alignItems": "center",
    "justifyContent": "space-between",
    "padding": "12px 8px 19px",

    [media.below.sm]: {
        width: "155px"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".gesture-icon": {
            animation: `${fadeIn} 0.5s ease-out forwards`
        }
    }
});
const teamCardActiveStyles = css({
    "border": `1px solid ${wacToken("color.primary.black")}`,
    "borderRadius": "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".gesture-icon": {
        opacity: 1
    }
});

const iconWrapperStyles = css({
    position: "relative"
});

const iconContainerStyles = css({
    height: "48px",
    width: "48px"
});

const iconGestureStyles = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    opacity: 0
});

const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: wacToken("color.primary.black")
});

const imageContainerStyles = css({
    width: "100%",
    aspectRatio: "16/9",
    position: "relative",
    marginBlockEnd: "8%"
});
const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "47%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: wacToken("color.primary.blue"),
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 16%,49% 10%,50% 26%,98.5% 13%,100% 85%,22.5% 100%,22.25% 85%,0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});

const heroImageStyling = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        opacity: 0,
        aspectRatio: "16/9",
        position: "absolute",
        maxWidth: "100%",
        maxHeight: "100%"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            animation: `${fadeIn} 0.3s ease-out forwards`
        }
    }
});

type TabsProps = {
    tabsWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

// Memoized TabButton component for better performance
const TabButton = memo(
    ({
        tab,
        index,
        isActive,
        onTabClick
    }: {
        tab: TWCTab;
        index: number;
        isActive: boolean;
        onTabClick: (index: number) => void;
    }) => {
        const intl = useIntl();

        return (
            <Adg4DraggableTab
                key={tab.id}
                tabIndex={index}
                onClick={() => onTabClick(index)}
            >
                <button
                    css={buttonResetStyle}
                    data-product-key="home"
                    data-event-component="button"
                    data-event-container="heroSection"
                    data-event="clicked"
                    data-label-english={tab.englishLabel}
                    aria-label={intl.formatMessage(tab.title)}
                >
                    <div
                        css={[teamCardStyles, isActive && teamCardActiveStyles]}
                    >
                        <div css={iconWrapperStyles}>
                            <div
                                css={[iconContainerStyles]}
                                className="default-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.icon,
                                        alt: "",
                                        draggable: "false",
                                        width: 48,
                                        height: 48
                                    }}
                                    forceEagerLoad={true} // Icons are small, load eagerly
                                />
                            </div>
                            <div
                                css={[iconContainerStyles, iconGestureStyles]}
                                className="gesture-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.iconGesture,
                                        alt: "",
                                        draggable: "false",
                                        width: 48,
                                        height: 48,
                                        loading: "eager",
                                        fetchpriority: "low"
                                    }}
                                />
                            </div>
                        </div>
                        <span css={teamCardTextStyles}>
                            {intl.formatMessage(tab.title)}
                        </span>
                    </div>
                </button>
            </Adg4DraggableTab>
        );
    }
);

TabButton.displayName = "TabButton";

// Memoized HeroImageSection component
const HeroImageSection = memo(
    ({ isCurrentTab }: { isCurrentTab: (index?: number) => boolean }) => {
        return (
            <Row layout="10">
                <div>
                    <div css={imageContainerStyles}>
                        <div css={heroImageStyling}>
                            {TWC_TAB_DATA.map((tab, index) => {
                                return (
                                    <Picture
                                        key={index}
                                        image={{
                                            src: tab.heroImage,
                                            alt: tab.englishLabel,
                                            width: tab.heroImageDimensions
                                                .imageWidth,
                                            height: tab.heroImageDimensions
                                                .imageHeight
                                        }}
                                        forceEagerLoad={index === 0} // Only first image loads eagerly
                                        className={
                                            isCurrentTab(index)
                                                ? "active-hero-image"
                                                : ""
                                        }
                                    />
                                );
                            })}
                        </div>
                        <div css={backgroundStyles} />
                    </div>
                </div>
            </Row>
        );
    }
);

HeroImageSection.displayName = "HeroImageSection";

export const TWCTabs = memo(({ tabsWrapperRef }: TabsProps) => {
    const { goToTab, isCurrentTab } = useAdg4TabsContext();

    const handleTabClick = useCallback(
        (index: number) => {
            goToTab(index);
        },
        [goToTab]
    );

    return (
        <>
            <div css={tabsWrapperStyle} ref={tabsWrapperRef}>
                <Adg4DraggableTabList css={[tabsStyle]}>
                    {TWC_TAB_DATA.map((tab, index) => (
                        <TabButton
                            key={tab.id}
                            tab={tab}
                            index={index}
                            isActive={isCurrentTab(index)}
                            onTabClick={handleTabClick}
                        />
                    ))}
                </Adg4DraggableTabList>
            </div>
            <HeroImageSection isCurrentTab={isCurrentTab} />
        </>
    );
});

TWCTabs.displayName = "TWCTabs";
